/**
 * useGuestRegion — Canonical region detection for all visitors.
 *
 * Priority differs by auth state:
 *
 * AUTHENTICATED (Katalog customer):
 *  1. Explicit user-selected country (localStorage)
 *  2. Logged-in customer default shipping address
 *  3. Vercel Edge cookie (katalog_geo)
 *  4. Cached detection (localStorage)
 *  5. Timezone heuristic
 *  6. Unknown
 *
 * GUEST (logged-out):
 *  1. Explicit user-selected country (localStorage)
 *  2. Vercel Edge cookie (katalog_geo)
 *  3. Cached detection (localStorage)
 *  4. Timezone heuristic
 *  5. Unknown
 *
 * Privacy-safe: no precise geolocation, country-level only.
 */
import { useState, useEffect } from 'react';
import { supabase } from '@bright-web-skeleton/supabase';
import { useAuth } from '@bright-web-skeleton/auth';

const EXPLICIT_KEY = 'katalog:selectedCountry'; // user explicitly chose
const DETECTED_KEY = 'katalog:guestRegion';     // auto-detected, cached

export type RegionSource = 'explicit' | 'address' | 'edge' | 'cached' | 'timezone' | 'unknown';

export interface GuestRegion {
  countryCode: string | null;
  territoryId: string | null;
  source: RegionSource;
  loading: boolean;
}

/** Read a cookie value by name */
function getCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

function getStored(key: string): string | null {
  try { return localStorage.getItem(key); } catch { return null; }
}

function setStored(key: string, value: string): void {
  try { localStorage.setItem(key, value); } catch { /* noop */ }
}

/** Coarse timezone → country mapping (covers ~90% of traffic) */
const TZ_COUNTRY_MAP: Record<string, string> = {
  'America/New_York': 'US', 'America/Chicago': 'US', 'America/Denver': 'US',
  'America/Los_Angeles': 'US', 'America/Phoenix': 'US', 'America/Anchorage': 'US',
  'Pacific/Honolulu': 'US', 'America/Detroit': 'US', 'America/Indiana/Indianapolis': 'US',
  'America/Boise': 'US', 'America/Juneau': 'US', 'America/Adak': 'US',
  'America/Toronto': 'CA', 'America/Vancouver': 'CA', 'America/Edmonton': 'CA',
  'America/Winnipeg': 'CA', 'America/Halifax': 'CA', 'America/St_Johns': 'CA',
  'America/Mexico_City': 'MX', 'America/Cancun': 'MX', 'America/Monterrey': 'MX',
  'America/Sao_Paulo': 'BR', 'America/Fortaleza': 'BR', 'America/Manaus': 'BR',
  'America/Argentina/Buenos_Aires': 'AR', 'America/Bogota': 'CO',
  'America/Santiago': 'CL', 'America/Lima': 'PE',
  'Europe/London': 'GB', 'Europe/Dublin': 'IE',
  'Europe/Paris': 'FR', 'Europe/Berlin': 'DE', 'Europe/Rome': 'IT',
  'Europe/Madrid': 'ES', 'Europe/Lisbon': 'PT',
  'Europe/Amsterdam': 'NL', 'Europe/Brussels': 'BE', 'Europe/Zurich': 'CH',
  'Europe/Vienna': 'AT', 'Europe/Warsaw': 'PL', 'Europe/Prague': 'CZ',
  'Europe/Budapest': 'HU', 'Europe/Bucharest': 'RO', 'Europe/Sofia': 'BG',
  'Europe/Athens': 'GR', 'Europe/Helsinki': 'FI', 'Europe/Stockholm': 'SE',
  'Europe/Oslo': 'NO', 'Europe/Copenhagen': 'DK',
  'Europe/Moscow': 'RU', 'Europe/Kiev': 'UA', 'Europe/Istanbul': 'TR',
  'Asia/Tokyo': 'JP', 'Asia/Seoul': 'KR', 'Asia/Shanghai': 'CN',
  'Asia/Hong_Kong': 'HK', 'Asia/Taipei': 'TW', 'Asia/Singapore': 'SG',
  'Asia/Kolkata': 'IN', 'Asia/Karachi': 'PK', 'Asia/Dhaka': 'BD',
  'Asia/Bangkok': 'TH', 'Asia/Jakarta': 'ID', 'Asia/Manila': 'PH',
  'Asia/Ho_Chi_Minh': 'VN', 'Asia/Kuala_Lumpur': 'MY',
  'Asia/Dubai': 'AE', 'Asia/Riyadh': 'SA', 'Asia/Tehran': 'IR',
  'Asia/Jerusalem': 'IL',
  'Australia/Sydney': 'AU', 'Australia/Melbourne': 'AU', 'Australia/Brisbane': 'AU',
  'Australia/Perth': 'AU', 'Australia/Adelaide': 'AU',
  'Pacific/Auckland': 'NZ',
  'Africa/Cairo': 'EG', 'Africa/Lagos': 'NG', 'Africa/Johannesburg': 'ZA',
  'Africa/Nairobi': 'KE', 'Africa/Casablanca': 'MA', 'Africa/Accra': 'GH',
  'Africa/Addis_Ababa': 'ET', 'Africa/Dar_es_Salaam': 'TZ',
};

function detectCountryFromTimezone(): string | null {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return TZ_COUNTRY_MAP[tz] ?? null;
  } catch { return null; }
}

/**
 * Set explicit user-selected country. Highest priority for all user types.
 * Call this from a region selector UI.
 */
export function setExplicitCountry(countryCode: string): void {
  setStored(EXPLICIT_KEY, countryCode.toUpperCase());
}

/**
 * Clear explicit selection (revert to auto-detection).
 */
export function clearExplicitCountry(): void {
  try { localStorage.removeItem(EXPLICIT_KEY); } catch { /* noop */ }
}

/** Resolve country from authenticated user's default shipping address */
async function resolveAddressCountry(userId: string): Promise<string | null> {
  // Try default address first
  const { data: addr } = await supabase
    .from('customer_addresses')
    .select('country_code')
    .eq('user_id', userId)
    .eq('is_default', true)
    .maybeSingle();

  if (addr?.country_code) return addr.country_code.toUpperCase();

  // Fallback: most recent address
  const { data: anyAddr } = await supabase
    .from('customer_addresses')
    .select('country_code')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  return anyAddr?.country_code?.toUpperCase() ?? null;
}

/** Resolve country from edge cookie */
function resolveEdgeCountry(): string | null {
  const edgeCountry = getCookie('katalog_geo');
  if (edgeCountry && edgeCountry.length === 2) {
    const upper = edgeCountry.toUpperCase();
    setStored(DETECTED_KEY, upper); // cache for faster subsequent loads
    return upper;
  }
  return null;
}

/** Apply result helper */
function makeResult(cc: string, source: RegionSource, tid: string | null): GuestRegion {
  return { countryCode: cc, territoryId: tid, source, loading: false };
}

export function useGuestRegion(): GuestRegion {
  const { authUser } = useAuth();
  const [region, setRegion] = useState<GuestRegion>({
    countryCode: null, territoryId: null, source: 'unknown', loading: true,
  });

  useEffect(() => {
    let cancelled = false;

    const resolve = async () => {
      const isAuthenticated = !!authUser?.id;

      // ── Priority 1 (all users): Explicit user selection ──
      const explicit = getStored(EXPLICIT_KEY);
      if (explicit) {
        const tid = await lookupTerritory(explicit);
        if (!cancelled) setRegion(makeResult(explicit, 'explicit', tid));
        return;
      }

      // ── Authenticated path: address before edge ──
      if (isAuthenticated) {
        // Priority 2 (auth): Default shipping address
        const addrCountry = await resolveAddressCountry(authUser!.id);
        if (cancelled) return;
        if (addrCountry) {
          setStored(DETECTED_KEY, addrCountry);
          const tid = await lookupTerritory(addrCountry);
          if (!cancelled) setRegion(makeResult(addrCountry, 'address', tid));
          return;
        }

        // Priority 3 (auth): Edge cookie
        const edge = resolveEdgeCountry();
        if (edge) {
          const tid = await lookupTerritory(edge);
          if (!cancelled) setRegion(makeResult(edge, 'edge', tid));
          return;
        }
      } else {
        // ── Guest path: edge before cached ──
        // Priority 2 (guest): Edge cookie
        const edge = resolveEdgeCountry();
        if (edge) {
          const tid = await lookupTerritory(edge);
          if (!cancelled) setRegion(makeResult(edge, 'edge', tid));
          return;
        }
      }

      // ── Shared fallbacks (both paths) ──

      // Cached detection
      const cached = getStored(DETECTED_KEY);
      if (cached) {
        const tid = await lookupTerritory(cached);
        if (!cancelled) setRegion(makeResult(cached, 'cached', tid));
        return;
      }

      // Timezone heuristic
      const tzCountry = detectCountryFromTimezone();
      if (tzCountry) {
        setStored(DETECTED_KEY, tzCountry);
        const tid = await lookupTerritory(tzCountry);
        if (!cancelled) setRegion(makeResult(tzCountry, 'timezone', tid));
        return;
      }

      // Unknown
      if (!cancelled) setRegion({ countryCode: null, territoryId: null, source: 'unknown', loading: false });
    };

    resolve();
    return () => { cancelled = true; };
  }, [authUser?.id]);

  return region;
}

/**
 * Look up territory UUID from country_code.
 * Prefers territory_type='country' to avoid ambiguity when
 * a country has many sub-territories (e.g. US has 50 states + 1 country row).
 * Falls back to first match if no country-level row exists.
 */
async function lookupTerritory(countryCode: string): Promise<string | null> {
  const upper = countryCode.toUpperCase();

  // Try country-level territory first (unambiguous)
  const { data: country } = await supabase
    .from('territories')
    .select('id')
    .eq('country_code', upper)
    .eq('territory_type', 'country')
    .maybeSingle();

  if (country?.id) return country.id;

  // Fallback: pick the first matching territory
  const { data: fallback } = await supabase
    .from('territories')
    .select('id')
    .eq('country_code', upper)
    .limit(1)
    .maybeSingle();

  return fallback?.id ?? null;
}
